/* eslint-disable react-hooks/immutability, react-hooks/set-state-in-effect -- this action bar mutates live link state and syncs renderer-driven UI state from effects. */
import * as React from "react";
import {flushSync} from "react-dom";
import styled from "styled-components";
import {useEffect, useReducer, useCallback, useRef, useState} from "react";

import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import type {Xyicon3D} from "../../../../modules/space/spaceeditor/logic3d/elements3d/Xyicon3D";
import type {LinkPropertiesUpdatedDto, PointDouble, UpdateLinkPropertyRequest} from "../../../../../generated/api/base";
import {LinkDirection, XyiconFeature} from "../../../../../generated/api/base";
import {THREEUtils} from "../../../../../utils/THREEUtils";
import type {Xyicon} from "../../../../../data/models/Xyicon";
import type {Link} from "../../../../../data/models/Link";
import InfoIcon from "../../../icons/info.svg?react";
import NoArrowIcon from "../../../icons/no-arrow-line.svg?react";
import UniDirectionalArrowLeftIcon from "../../../icons/uni-directional-arrow-left.svg?react";
import UniDirectionalArrowRightIcon from "../../../icons/uni-directional-arrow-right.svg?react";
import BiDirectionalArrowIcon from "../../../icons/bi-directional-arrow.svg?react";
import BreakLinkIcon from "../../../icons/break-link.svg?react";

import {colorPalette} from "../../../styles/colorPalette";
import type {SpaceItem} from "../../../../modules/space/spaceeditor/logic3d/elements3d/SpaceItem";
import type {TransportLayer} from "../../../../../data/TransportLayer";
import {ConfirmWindowV5} from "../../../popup/ConfirmWindowV5";
import {XHRLoader} from "../../../../../utils/loader/XHRLoader";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import {SpaceActionBarStyled} from "../spaceactionbar/SpaceActionBar.styled";
import {DropdownButtonV5} from "../../../interaction/DropdownButtonV5";
import {HorizontalAlignment, VerticalAlignment} from "../../../../../utils/dom/DomUtils";
import {Functions} from "../../../../../utils/function/Functions";
import {baseDistance} from "../../../styles/styles";

const ACTION_BAR_STYLE = {
	position: "absolute" as const,
	gap: "8px",
	zIndex: "1",
	width: "208px",
	height: "48px",
	color: colorPalette.white,
	backgroundColor: colorPalette.gray.c950,
	boxShadow: "0 2px 4px rgba(0, 0, 0, 0.25)",
};

const DIRECTION_ICON_STYLE = {
	width: "16px",
	height: "16px",
} as const;

const CROSS_PORTFOLIO_CONFIRMATION_MESSAGE =
	"The xyicon linked is located in a different portfolio. If you break this link, it will affect the relationship of the xyicons across both portfolios. Are you certain you want to proceed?";

interface ILinkBreakersProps {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly fromObjectIds: string[];
	readonly toggleDetailsPanel: () => void;
	readonly isDetailsPanelOpen: boolean;
	readonly forceUpdateDetailsPanel: () => void;
}

export interface ILineSet {
	fromXyicon: Xyicon3D;
	toXyicons: Xyicon3D[];
	link?: Link;
}

interface IFilteredLinkObject {
	objectAID: string;
	link: Link;
	toXyicons: Xyicon3D;
}

const DIRECTION_ICON_MAP = {
	[LinkDirection.None]: NoArrowIcon,
	[LinkDirection.AtoB]: UniDirectionalArrowRightIcon,
	[LinkDirection.BtoA]: UniDirectionalArrowLeftIcon,
	[LinkDirection.Both]: BiDirectionalArrowIcon,
} as const;

export const breakLinks = async (
	transport: TransportLayer,
	linkIds: string[],
	isCrossPortfolioConfirmNeeded = false,
	portfolioId = transport.appState.activePortfolioId,
) => {
	if (isCrossPortfolioConfirmNeeded) {
		const isConfirmed = await ConfirmWindowV5.open(CROSS_PORTFOLIO_CONFIRMATION_MESSAGE, "Confirming Breaking Link", {ok: "Yes", cancel: "Cancel"});
		if (!isConfirmed) {
			return;
		}
	}

	return transport.requestForOrganization({
		url: "xyicons/deletelink",
		method: XHRLoader.METHOD_DELETE,
		params: {
			portfolioID: portfolioId,
			linkIDList: linkIds,
		},
	});
};

export const LinkLineActionBarV5 = ({
	spaceViewRenderer,
	fromObjectIds,
	toggleDetailsPanel,
	isDetailsPanelOpen,
	forceUpdateDetailsPanel,
}: ILinkBreakersProps) => {
	const {spaceItemController, signals, toolManager} = spaceViewRenderer;
	const selectedLink = spaceItemController.linkLineManager.getSelectedLink();
	const fromXyiconsRef = useRef<Xyicon3D[]>([]);
	const ref = useRef<HTMLDivElement>(null);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const [filteredLinkObjects, setFilteredLinkObjects] = useState<IFilteredLinkObject[]>([]);

	const getStyle = useCallback(
		(fromPosition: PointDouble, toPosition: PointDouble) => {
			const baseStyle = THREEUtils.getStyleForFloatingUIElement(
				(fromPosition.x + toPosition.x) / 2,
				(fromPosition.y + toPosition.y) / 2 + 0.6,
				spaceViewRenderer.spaceOffset.z,
				spaceViewRenderer,
				true,
				"center",
			);

			return {
				...baseStyle,
				...ACTION_BAR_STYLE,
			};
		},
		[spaceViewRenderer],
	);

	const getDirectionIcon = useCallback((direction: LinkDirection) => {
		return DIRECTION_ICON_MAP[direction] || BiDirectionalArrowIcon;
	}, []);

	const getFilteredLinkObjects = useCallback(() => {
		const result: {objectAID: string; link: Link; toXyicons: SpaceItem}[] = [];
		const seenLinkIds = new Set<string>();

		for (const objectAID of fromObjectIds) {
			const links = spaceViewRenderer.actions.getLinksXyiconXyicon(objectAID);

			for (const linkData of links) {
				const {link, object} = linkData;

				if (link.isEmbedded || (object as Xyicon).spaceId !== spaceViewRenderer.space?.id || seenLinkIds.has(link?.id)) {
					continue;
				}

				const toXyicon = spaceViewRenderer.xyiconManager.getItemById(object.id);
				if (!toXyicon?.isVisible) {
					continue;
				}

				result.push({objectAID, link, toXyicons: toXyicon});
				seenLinkIds.add(link.id);
			}
		}

		return result;
	}, [fromObjectIds, spaceViewRenderer]);

	const resetLinkIconSelection = useCallback(() => {
		fromXyiconsRef.current.forEach((fromXyicon) => {
			spaceItemController.linkIconManager.deselectIcon(fromXyicon.linkInstanceId);
		});
	}, [spaceItemController]);

	const reset3DElements = useCallback(() => {
		spaceItemController.linkLineManager.deselectAll();
		spaceItemController.linkLineManager.destroyAll();
		resetLinkIconSelection();
	}, [spaceItemController, resetLinkIconSelection]);

	const close = useCallback(() => {
		requestAnimationFrame(() => {
			reset3DElements();
			spaceItemController.closeLinks();
		});
	}, [reset3DElements, spaceItemController]);

	const createLineSetsFromFilteredLinkObjects = useCallback(
		(filteredObjects: {objectAID: string; link: Link; toXyicons: SpaceItem}[]): ILineSet[] => {
			return filteredObjects.map((obj) => ({
				fromXyicon: fromXyiconsRef.current.find((x) => x.id === obj.objectAID)!,
				toXyicons: [obj.toXyicons as Xyicon3D],
				link: obj.link,
			}));
		},
		[],
	);

	const updateConnectionLines = useCallback(
		(lineSets: ILineSet[]) => {
			spaceItemController.linkLineManager.update(lineSets);
			lineSets.forEach((lineSet) => {
				spaceItemController.linkIconManager.selectIcon(lineSet.fromXyicon.linkInstanceId);
			});
		},
		[spaceItemController],
	);

	const forceUpdateArrow = useCallback(() => {
		if (fromXyiconsRef.current.length > 0) {
			const newFiltered = getFilteredLinkObjects();
			if (newFiltered.length > 0) {
				const lineSets = createLineSetsFromFilteredLinkObjects(newFiltered);
				updateConnectionLines(lineSets);
			} else {
				// If no filtered objects remain, clear all existing lines
				spaceItemController.linkLineManager.destroyAll();
			}
		}
		flushSync(forceUpdate);
	}, [getFilteredLinkObjects, createLineSetsFromFilteredLinkObjects, updateConnectionLines, spaceItemController.linkLineManager]);

	const breakLink = useCallback(
		async (linkId: string) => {
			await breakLinks(spaceViewRenderer.transport, [linkId]);
			const appState = spaceViewRenderer.transport.appState;
			appState.lists[XyiconFeature.Link].deleteById(linkId);

			// Immediately clear filtered objects to prevent stale state during transition
			setFilteredLinkObjects((prev) => prev.filter((obj) => obj.link.id !== linkId));

			spaceViewRenderer.spaceItemController.linkIconManager.update();
			// Simply re-render the space to reflect the updated state
			spaceViewRenderer.needsRender = true;
			forceUpdateArrow();
		},
		[spaceViewRenderer, forceUpdateArrow],
	);

	const updateLinkDirection = useCallback(
		async (linkId: string, direction: LinkDirection) => {
			try {
				const link = spaceViewRenderer.actions.getFeatureItemById<Link>(linkId, XyiconFeature.Link);
				if (link?.data.linkProperties) {
					link.data.linkProperties.direction = direction;
				}

				spaceItemController.linkLineManager.updateLinkProperties(linkId, {direction});

				forceUpdateArrow();

				const params: UpdateLinkPropertyRequest = {
					linkID: linkId,
					linkProperties: {
						direction,
						settings: link?.data.linkProperties?.settings,
					},
				};

				const {result} = await spaceViewRenderer.transport.requestForOrganization<LinkPropertiesUpdatedDto>({
					url: "links/updateproperties",
					method: XHRLoader.METHOD_PUT,
					params: params,
				});

				link.applyData?.({
					lastModifiedAt: result.lastModifiedAt ?? link.data.lastModifiedAt,
					lastModifiedBy: result.lastModifiedBy ?? link.data.lastModifiedBy,
				});

				forceUpdateDetailsPanel();
			} catch (error) {
				console.error("Failed to update link direction:", error);
			}
		},
		[spaceViewRenderer, spaceItemController, forceUpdateArrow, forceUpdateDetailsPanel],
	);

	const getDirectionOptions = useCallback(
		(linkId: string, currentDirection?: LinkDirection) => [
			{
				key: "both",
				isActive: currentDirection === LinkDirection.Both,
				IconComponent: getDirectionIcon(LinkDirection.Both),
				onClick: () => updateLinkDirection(linkId, LinkDirection.Both),
				iconCustomStyle: {width: "18px", height: "18px"}, // Updated as per reqest from design team only for both directionality icon
			},
			{
				key: "atob",
				isActive: currentDirection === LinkDirection.AtoB,
				IconComponent: getDirectionIcon(LinkDirection.AtoB),
				onClick: () => updateLinkDirection(linkId, LinkDirection.AtoB),
				iconCustomStyle: DIRECTION_ICON_STYLE,
			},
			{
				key: "btoa",
				isActive: currentDirection === LinkDirection.BtoA,
				IconComponent: getDirectionIcon(LinkDirection.BtoA),
				onClick: () => updateLinkDirection(linkId, LinkDirection.BtoA),
				iconCustomStyle: DIRECTION_ICON_STYLE,
			},
			{
				key: "none",
				isActive: currentDirection === LinkDirection.None,
				IconComponent: getDirectionIcon(LinkDirection.None),
				onClick: () => updateLinkDirection(linkId, LinkDirection.None),
				iconCustomStyle: DIRECTION_ICON_STYLE,
			},
		],
		[getDirectionIcon, updateLinkDirection],
	);

	useEffect(() => {
		const closeDetailsPanelCallback = () => {
			if (isDetailsPanelOpen) {
				toggleDetailsPanel();
			}
		};
		spaceItemController.linkLineManager.setCloseDetailsPanelCallback(closeDetailsPanelCallback);

		const actionBarUpdateCallback = () => {
			flushSync(forceUpdate);
		};
		spaceItemController.linkLineManager.setUpdateActionBarCallback(actionBarUpdateCallback);

		return () => {
			spaceItemController.linkLineManager.clearCloseDetailsPanelCallback();
			spaceItemController.linkLineManager.clearUpdateActionBarCallback();
		};
	}, [
		isDetailsPanelOpen,
		toggleDetailsPanel,
		spaceItemController.linkLineManager,
		getFilteredLinkObjects,
		createLineSetsFromFilteredLinkObjects,
		updateConnectionLines,
	]);

	useEffect(() => {
		signals.onCanvasResized.add(forceUpdateArrow);
		spaceItemController.signals.itemsTranslated.add(forceUpdateArrow);
		toolManager.cameraControls.signals.cameraPropsChange.add(forceUpdateArrow);

		// Listen to SignalR link updates for cross-browser synchronization
		const handleLinksUpdated = (data: {links: any[]; type: string}) => {
			if (data.type === "deleted") {
				// When links are deleted from other browser instances, simply re-render
				spaceViewRenderer.needsRender = true;
				forceUpdateArrow();
			}
		};

		spaceViewRenderer.transport.signalR.listener.signals.linksUpdated.add(handleLinksUpdated);

		return () => {
			signals.onCanvasResized.remove(forceUpdateArrow);
			spaceItemController.signals.itemsTranslated.remove(forceUpdateArrow);
			toolManager.cameraControls.signals.cameraPropsChange.remove(forceUpdateArrow);
			spaceViewRenderer.transport.signalR.listener.signals.linksUpdated.remove(handleLinksUpdated);
			reset3DElements();
		};
	}, [forceUpdateArrow, reset3DElements, signals, spaceItemController, toolManager, spaceViewRenderer]);

	useEffect(() => {
		fromXyiconsRef.current = fromObjectIds.map((id) => spaceViewRenderer.xyiconManager.getItemById(id) as Xyicon3D).filter(Boolean);

		if (fromXyiconsRef.current.length === 0) {
			close();
			return;
		}

		const filtered = getFilteredLinkObjects();
		if (filtered.length === 0) {
			close();
			return;
		}

		setFilteredLinkObjects(filtered as IFilteredLinkObject[]);
		const lineSets = createLineSetsFromFilteredLinkObjects(filtered);
		updateConnectionLines(lineSets);
	}, [fromObjectIds, spaceViewRenderer, getFilteredLinkObjects, createLineSetsFromFilteredLinkObjects, updateConnectionLines, close]);

	// Update connection lines on every render (since parent re-renders trigger this)
	useEffect(() => {
		const filtered = getFilteredLinkObjects();
		if (filtered.length > 0) {
			updateConnectionLines(createLineSetsFromFilteredLinkObjects(filtered));
		}
	});

	return (
		<>
			{filteredLinkObjects.map((obj) => {
				const displayFromXyicon = fromXyiconsRef.current.find((x) => x.id === obj.objectAID);
				if (!displayFromXyicon || obj.link.id !== selectedLink?.link.id) {
					return <React.Fragment key={obj.link.id} />;
				}

				const fromPosition = displayFromXyicon.position;
				const displayToXyicon = obj.toXyicons;
				const toPosition = displayToXyicon.position;

				// Additional safety checks to prevent rendering during deletion transitions
				// Check 1: Verify that positions are valid
				if (
					!fromPosition ||
					!toPosition ||
					typeof fromPosition.x !== "number" ||
					typeof fromPosition.y !== "number" ||
					typeof toPosition.x !== "number" ||
					typeof toPosition.y !== "number"
				) {
					return <React.Fragment key={obj.link.id} />;
				}

				// Check 2: Verify that the link still exists in app state
				const linkStillExists = spaceViewRenderer.actions.getFeatureItemById(obj.link.id, XyiconFeature.Link);
				if (!linkStillExists) {
					return <React.Fragment key={obj.link.id} />;
				}

				const linkObjectAID = obj.link.data.objectAID;
				const linkObjectBID = obj.link.data.objectBID;

				const actualObjectAXyicon =
					fromXyiconsRef.current.find((x) => x.id === linkObjectAID) || (displayToXyicon.id === linkObjectAID ? displayToXyicon : null);
				const actualObjectBXyicon =
					fromXyiconsRef.current.find((x) => x.id === linkObjectBID) || (displayToXyicon.id === linkObjectBID ? displayToXyicon : null);

				if (!actualObjectAXyicon || !actualObjectBXyicon) {
					return <React.Fragment key={obj.link.id} />;
				}

				const firstThumbnail = actualObjectAXyicon.modelData.thumbnail;
				const secondThumbnail = actualObjectBXyicon.modelData.thumbnail;
				const firstTooltip = actualObjectAXyicon.modelData.refId;
				const secondTooltip = actualObjectBXyicon.modelData.refId;

				const FirstThumbnailIcon = () => (
					<img
						src={firstThumbnail}
						alt="thumbnail"
						style={{
							width: "32px",
							height: "32px",
						}}
					/>
				);

				const SecondThumbnailIcon = () => (
					<img
						src={secondThumbnail}
						alt="thumbnail"
						style={{
							width: "32px",
							height: "32px",
						}}
					/>
				);

				return (
					<LinkLineActionBarStyled
						key={obj.link.id}
						ref={ref}
						style={getStyle(fromPosition, toPosition)}
					>
						<ThumbnailContainer>
							<IconButtonV5
								IconComponent={FirstThumbnailIcon}
								title={firstTooltip}
								onClick={Functions.emptyFunction}
								isActive={false}
								className="thumbnailButton"
							/>
							<DropdownButtonV5
								button={
									<IconButtonV5
										IconComponent={getDirectionIcon(obj.link.data.linkProperties?.direction)}
										title="Directionality"
										onClick={Functions.emptyFunction}
										isActive={false}
									/>
								}
								options={getDirectionOptions(obj.link.id, obj.link.data.linkProperties?.direction)}
								horizontalAlignment={HorizontalAlignment.center}
								verticalAlignment={VerticalAlignment.bottomOuter}
								className="linkDirectionDropdown"
								style={{minWidth: "48px"}}
								onlyIcons={true}
							/>
							<IconButtonV5
								IconComponent={SecondThumbnailIcon}
								title={secondTooltip}
								onClick={Functions.emptyFunction}
								isActive={false}
								className="thumbnailButton"
							/>
						</ThumbnailContainer>
						<IconButtonV5
							IconComponent={InfoIcon}
							title="Details"
							onClick={toggleDetailsPanel}
							isActive={isDetailsPanelOpen}
						/>
						<IconButtonV5
							IconComponent={BreakLinkIcon}
							title="Break Link"
							onClick={() => breakLink(obj.link.id)}
						/>
					</LinkLineActionBarStyled>
				);
			})}
		</>
	);
};

const LinkLineActionBarStyled = styled(SpaceActionBarStyled)`
	gap: ${baseDistance.sm};
`;

const ThumbnailContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: ${baseDistance.sm};

	.thumbnail {
		width: "32px";
		height: "32px";
	}

	.thumbnailButton {
		min-width: "32px";
		width: "32px";
		height: "32px";
		padding: 0;
		border-radius: 4px;
		overflow: hidden;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		&:hover {
			cursor: default;
		}
	}
`;
